import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import Form from "src/components/base/form";
import { useForm, useStore } from "@tanstack/react-form";
import React from "react";
import { toast } from "react-toastify";
import { Description, ErrorMessage, Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Divider } from "src/components/base/divider";
import { Button, PrimaryButton } from "src/components/base/button";
import { ApiInternal } from "src/api/api_internal";
import { Switch, SwitchField } from "src/components/base/switch";
import BlockUnsavedChanges from "src/components/base/unsaved-changes";

/**
 * The properties for {@link SettingGeneral}
 */
export type SettingGeneralProps = {};

/**
 * Setting page to modify general server/network configuration
 */
function SettingGeneral(props: SettingGeneralProps) {
    const [t] = useTranslation("admin-settings");
    const [tg] = useTranslation();

    const router = useRouter();
    const data = Route.useLoaderData();

    const form = useForm({
        defaultValues: {
            enabled: data.enabled,
            token: data.token,
            entra_workaround: data.entra_workaround,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (value.enabled && value.token.length === 0) {
                    return { fields: { token: t("error.token-empty") } };
                }

                await ApiInternal.settings.setScim({
                    enabled: value.enabled,
                    token: value.token,
                    entra_workaround: value.entra_workaround,
                });

                toast.success(tg("toast.saved-successfully"));
                router.invalidate();
                form.reset();
            },
        },
    });
    const isDirty = useStore(form.store, (store) => store.isDirty);

    return (
        <div className={"flex flex-col justify-start gap-4"}>
            <BlockUnsavedChanges condition={isDirty} />
            <Form className={"flex max-w-lg flex-col gap-12"} onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field name={"enabled"}>
                            {(fieldApi) => (
                                <SwitchField className={"mb-4"}>
                                    <Label>{t("label.enable-scim")}</Label>
                                    <Switch
                                        color={"blue"}
                                        checked={fieldApi.state.value}
                                        onChange={fieldApi.handleChange}
                                    />
                                    <Description>{t("description.enable-scim")}</Description>
                                </SwitchField>
                            )}
                        </form.Field>
                        <form.Field name={"token"}>
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.scim-token")}</Label>
                                    <Description>{t("description.scim-token")}</Description>
                                    <Input
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        invalid={fieldApi.state.meta.errors.length > 0}
                                    />
                                    {fieldApi.state.meta.errors.map((err) => (
                                        <ErrorMessage key={err}>{err}</ErrorMessage>
                                    ))}
                                </Field>
                            )}
                        </form.Field>
                        <form.Field name={"entra_workaround"}>
                            {(fieldApi) => (
                                <SwitchField className={"mb-4"}>
                                    <Label>{t("label.entra-workaround")}</Label>
                                    <Switch
                                        color={"blue"}
                                        checked={fieldApi.state.value}
                                        onChange={fieldApi.handleChange}
                                    />
                                    <Description>{t("description.entra-workaround")}</Description>
                                </SwitchField>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>
                <Divider />
                <div className={"flex justify-end gap-3"}>
                    <Button plain={true} disabled={!isDirty} onClick={() => form.reset()}>
                        {t("button.reset")}
                    </Button>
                    <PrimaryButton type="submit">{t("button.save")}</PrimaryButton>
                </div>
            </Form>
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/settings/_settings/scim")({
    component: SettingGeneral,
    loader: async () => await ApiInternal.settings.getScim(),
});
